import { clipLogin } from "./http/api";
import { request, requestV2 } from "./index";
import { deviceType } from "@config";

const fetchConfigs = async <Table>({ reqData }: { reqData: string[] }) => {
  const data = reqData.map((item) => ({ key: item }));
  return request<Table>({ method: "POST", url: "/api/configurations", data: data });
};

const signup = async (data: {
  phoneCountryCode?: string;
  phoneNumber?: string;
  currency?: string;
  smsCode?: string;
  tcCode?: string;
  email?: string;
  emailCode?: string;
  loginMethod?: string; // EMAIL
  scene?: string;
}) => {
  delete data.scene;
  return request({
    method: "post",
    url: "/api/login",
    data,
  });
};

const login = async (data: {
  phoneCountryCode?: string;
  phoneNumber?: string;
  smsCode?: string;
  loginMethod: "PASSWORD" | "EMAIL_PASSWORD" | "EMAIL" | "SMS" | "GOOGLE";
  password?: string;
  tcCode?: string;
  currency?: string;
  email?: string;
  emailCode?: string;
  scene?: string;
}) => {
  delete data.scene;
  return clipLogin({ body: data as unknown as any, query: {}, context: { silent: true } });
};

const refreshToken = async () => {
  return request({
    method: "post",
    url: "/api/token/refresh",
  });
};

const tiktokLogin = async (data: { code: string; type: string; client?: string }) => {
  return request({
    method: "post",
    url: "/api/login",
    data: {
      code: data.code,
      loginMethod: data.client || "TIKTOK",
    },
  });
};

const googleLogin = async (data: { code: string; type: string; client?: string }) => {
  return request({
    method: "post",
    url: "/api/login",
    data: {
      code: data.code,
      loginMethod: data.client || "GOOGLE",
    },
  });
};

const verifyToken = async (data: { token: string }) => {
  return request({
    method: "post",
    url: "/api/oauth2/kalo",
    data,
  });
};

const verifyAccount = async (data: {
  loginMethod: "SMS" | "TIKTOK" | "EMAIL" | "GOOGLE";
  phoneCountryCode?: string;
  phoneNumber?: string;
  smsCode?: string;
  code?: string;
  currentPhoneNumber?: string;
  email?: string;
  emailCode?: string;
}) => {
  return request({
    method: "post",
    url: "/api/users/verify",
    data,
  });
};

const sendSmsCode = async (data: {
  scene?: "signup" | "login" | "reset" | "verify" | "resetPassword";
  countryCode?: string;
  phoneNumber?: string;
  email?: string;
  response: string;
  captchaType?: string;
}) => {
  const isEmail = !!data.email;
  delete data.scene;
  return request({
    method: "post",
    url: isEmail ? "/api/captcha/email-send" : "/api/captcha/sms-send",
    data,
  });
};

// Customer Service
const fetchCustomerServiceContact = async () => {
  return requestV2<CustomerServiceContact>({ url: `/v1/countryV1/contacts`, params: { type: deviceType } });
};

// Reset Password
export interface VerifyForResetPasswordRequest {
  loginMethod: "EMAIL" | "SMS";
  email?: string;
  emailCode?: string;
  phoneCountryCode?: string;
  phoneNumber?: string;
  smsCode?: string;
}

const verifyForResetPassword = async (data: VerifyForResetPasswordRequest) => {
  return requestV2({
    method: "post",
    url: `/user/verifyForResetPassword`,
    data: {
      // scene: "resetPassword",
      ...data,
    },
  });
};

const resetPassword = async (password: string) => {
  return requestV2({ method: "post", url: `/user/resetPassword`, data: { password } });
};

const checkVerification = (data: {
  scene?: "signup" | "login" | "reset" | "addSubAccount" | "verify";
  phoneNumber?: string;
  smsCode?: string;
  phoneCountryCode?: string;
  loginMethod?: string;
  email?: string;
  emailCode?: string;
}) => {
  let url = "/api/checkVerification";
  if (data.email && data.scene === "login") url = "/email/checkLoginVerification";
  if (data.email && data.scene === "signup") url = "/email/checkSignupVerification";
  delete data.scene;
  return request({
    method: "post",
    url: url,
    data,
  });
};

const checkPhoneNumberIsExist = (data: { phoneCountryCode: string; phoneNumber: string }) => {
  return request({
    method: "post",
    url: "/user/checkPhoneNumberIsExist",
    data,
  });
};

const checkEmailIsExist = (data: { email: string }) => {
  return request({
    method: "post",
    url: "/user/checkEmailIsExist",
    data,
  });
};

const modifyProfile = async (data: {
  identity?: "CREATOR" | "SELLER" | "AGENCY";
  language?: string; // 'en-US'
  currency?: string; //'USD'
  industry?: string[]; //[1234]
  requirementIds?: string;
  platform?: string;
  referralCode?: string;
  password?: string;
  firstName?: string;
  lastName?: string;
}) => {
  return request({
    method: "post",
    url: "/api/users/profile",
    data: data,
  });
};

const logout = () => {
  return request({
    method: "post",
    url: `/api/logout`,
  });
};

const queryMembership = async <T>(reqData: unknown) => {
  return request<T>({ method: "post", url: `/user/queryMembership`, data: reqData });
};

const queryMemberships = async <T>() => {
  return request<T>({ method: "post", url: `/user/queryAllMemberships` });
};

const queryDevices = async <T>(reqData: unknown) => {
  return request<T>({ method: "post", url: `/api/queryDevicesByPhoneNumber`, data: reqData });
};

const checkGoogleGrecaptcha = async <T>(reqData: { captcha: string; captchaType?: string }) => {
  return request<T>({
    method: "post",
    url: `/user/checkVerifyCode`,
    data: reqData,
  });
};

const queryAffiliateInfo = async (reqData: { productList: { productId: string; skuId: string }[] }) => {
  return request<{ discountCode: string }>({ method: "post", url: `/user/queryAffiliateInfo`, data: reqData });
};

const getSubscriptionList = async <T>() => {
  return request<T>({ method: "get", url: `/user/subscription/list` });
};

const cancelSubscription = async (reqData: unknown) => {
  return request<{ id: string }>({ method: "post", url: `/user/subscription/cancel`, data: reqData });
};

const deleteSubscription = async (reqData: unknown) => {
  return request<{ id: string }>({ method: "post", url: `/user/subscription/delete`, data: reqData });
};

const paySubscription = async (reqData: unknown) => {
  return request<string>({ method: "post", url: `/user/subscription/pay`, data: reqData });
};

const getVideoPath = async (reqData: unknown) => {
  return request<string>({
    method: "get",
    url: "/video/detail/getVideoPath",
    params: reqData,
  });
};

const getGlobalNotify = async () => {
  return request({
    method: "post",
    url: "/homepage/dialog/queryList",
  });
};

const getTitleTranslate = async (reqData: { text: string; to: string }) => {
  return request<string>({
    method: "post",
    url: "/api/translation/translate",
    data: reqData,
  });
};

const updatePageTabs = async (reqData: { list: number[]; type: string }) => {
  return request<string>({
    method: "post",
    url: "/filterGroup/savePageFilter",
    data: reqData,
  });
};

// 增加微信登录
// 获取微信二维码
const getWxQrUrl = async () => {
  return request<{ sceneStr: string; qrCodeUrl: string }>({
    method: "get",
    url: "/user/wechat/qrUrl",
  });
};

// 轮训登录状态
const getWxLoginToken = async (reqData: { sceneStr: string }) => {
  return request<{ logined: boolean; success: boolean; exist: boolean; wxOpenId: string; wxUnionId: string }>({
    method: "get",
    url: "/user/wechat/getLoginToken",
    params: reqData,
  });
};
// 广告绑定微信
const fetchBindWechat = async (reqData: { sceneStr: string }) => {
  return request<{
    logined: boolean;
    success: boolean;
    exist: boolean;
    wxOpenId: string;
    wxUnionId: string;
    used: boolean;
  }>({
    method: "get",
    url: "/user/bindWechat",
    params: reqData,
  });
};

// Google Ads 相关接口
const signGoogleAds = async () => {
  return request<{ data: Record<string, any> }>({
    method: "get",
    url: "/gooads/signGoogleAds",
  });
};

const adsCallback = async (reqData: { key: string }) => {
  return request({
    method: "get",
    url: `/gooads/adsCallback?orderIds=${reqData.key}`,
  });
};

const getDownloadVideoURL = async (reqData: { videoId: string; videoSource: string }) => {
  return request({
    method: "get",
    url: `/api/video/download-url`,
    params: reqData,
  });
};

// 中国用户查询tcCode 是否有领取会员
const queryTcCode = async () => {
  return request<{
    status: 0 | 1 | 2; // 0 没有活动，1 暂未领取 ；2 已经领取
    level: "basic" | "standard" | "premium" | ""; // 会员等级 | standard
    day: number; // 活动天数
  }>({ method: "get", url: `/tcCode/query` });
};

// url:/tcCode/claim
// 中国用户领取会员
const claimTcCode = async () => {
  return request({ method: "get", url: `/tcCode/claim` });
};

// 获取媒体上传协议内容
const getMediaAgreement = async <T = string[]>() => {
  return request<T>({ method: "get", url: "/api/users/media-agreement" });
};

// 确认媒体上传协议
const confirmMediaAgreement = async (data: { version: string }) => {
  return request({ method: "post", url: `/api/users/media-agreement`, data });
};

export default {
  fetchConfigs,
  login,
  refreshToken,
  tiktokLogin,
  googleLogin,
  verifyToken,
  signup,
  modifyProfile,
  logout,
  sendSmsCode,
  checkVerification,
  queryMembership,
  queryMemberships,
  queryDevices,
  checkGoogleGrecaptcha,
  verifyAccount,
  checkPhoneNumberIsExist,
  checkEmailIsExist,
  queryAffiliateInfo,
  getSubscriptionList,
  cancelSubscription,
  paySubscription,
  getVideoPath,
  deleteSubscription,
  getGlobalNotify,
  getTitleTranslate,
  updatePageTabs,
  getWxQrUrl,
  getWxLoginToken,
  fetchBindWechat,
  signGoogleAds,
  adsCallback,
  getDownloadVideoURL,
  queryTcCode,
  claimTcCode,
  getMediaAgreement,
  confirmMediaAgreement,
  // V1 APIs
  fetchCustomerServiceContact,
  verifyForResetPassword,
  resetPassword,
};
